import React from 'react'
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router'
import { Typography, Space, Button, theme, GlobalToken } from 'antd'
import { HomeOutlined, ArrowLeftOutlined } from '@ant-design/icons'

interface ErrorPageStyles {
  root: React.CSSProperties
  container: React.CSSProperties
  errorCode: React.CSSProperties
  errorMessage: React.CSSProperties
  actions: React.CSSProperties
  debugData: React.CSSProperties
}

const errorPageStyles = (token: GlobalToken): ErrorPageStyles => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: token.colorBgLayout,
    padding: token.paddingLG,
  },
  container: {
    maxWidth: 600,
    textAlign: 'center',
    width: '100%',
  },
  errorCode: {
    fontSize: '6rem',
    fontWeight: 700,
    color: token.colorPrimary,
    marginBottom: token.marginLG,
    lineHeight: 1,
  },
  errorMessage: {
    fontSize: '1.5rem',
    color: token.colorText,
    marginBottom: token.marginLG,
  },
  actions: {
    marginTop: token.marginXXL,
    display: 'flex',
    justifyContent: 'center',
    gap: token.marginMD,
  },
  debugData: {
    marginTop: token.marginXXL,
    textAlign: 'left',
    backgroundColor: '#f5f5f5',
    padding: token.paddingLG,
    borderRadius: token.borderRadius,
  },
})

export default function ErrorPage() {
  const error = useRouteError()
  const navigate = useNavigate()
  const { token } = theme.useToken()
  const styles = errorPageStyles(token)

  const isDevelopment = process.env.NODE_ENV === 'development'

  let errorCode = '404'
  let errorMessage = 'Page Not Found'
  let errorDetails = ''

  if (isRouteErrorResponse(error)) {
    errorCode = error.status.toString()
    errorMessage = error.statusText || 'An error occurred'
    errorDetails = error.data?.message || JSON.stringify(error.data, null, 2) || ''
  } else if (error instanceof Error) {
    errorMessage = error.message
    errorDetails = error.stack || ''
  } else {
    errorDetails = JSON.stringify(error, null, 2)
  }

  const handleGoHome = () => {
    navigate('/')
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  return (
    <div style={styles.root}>
      <div style={styles.container}>
        <div style={styles.errorCode}>{errorCode}</div>

        <Typography.Title level={2} style={styles.errorMessage}>
          {errorMessage}
        </Typography.Title>

        <Typography.Paragraph type="secondary">
          The page you&#39;re looking for doesn&#39;t exist or an error occurred.
        </Typography.Paragraph>

        <Space style={styles.actions}>
          <Button
            type="primary"
            icon={<HomeOutlined />}
            onClick={handleGoHome}
            size="large"
          >
            Go Home
          </Button>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleGoBack}
            size="large"
          >
            Go Back
          </Button>
        </Space>

        {isDevelopment && errorDetails && (
          <pre style={styles.debugData}>{errorDetails}</pre>
        )}
      </div>
    </div>
  )
}

